/**
 * Shared share block: the link, the editable message, and the channels.
 *
 * Extracted from the Travel Impact Calculator's ShareButton dialog (anonymous
 * access v1 §7) so the calculator and the trip share dialogs offer the same
 * channels with one implementation. Layout (UX decision, Paulo 2026-10-03):
 *   • Link row with **Copy link**: the main action. With link previews, a bare
 *     link already shows up as a card in WhatsApp, Telegram and Slack.
 *   • Password row (password-protected trips) with its own copy button; the
 *     "Link copied" toast reminds the user to send the password too.
 *   • The editable message with **Copy message** (secondary).
 *   • Email · WhatsApp (wa.me) · X (tweet intent) send the message. Web Share
 *     on coarse-pointer (touch) devices only; desktop browsers render an
 *     awkward half-screen panel.
 *   • Email: with `sendEmail` (signed-in accounts on trips), the tile opens an
 *     inline "Send by email" panel: addresses plus an optional note, and Jody
 *     emails a link to the trip in the user's name (its own layout, button
 *     first; the chat message above is not used, so every link in the email
 *     comes from the server), with "Open my email app instead" one click away. Without it (guests,
 *     anonymous visitors, the calculator) the tile opens the email app, as
 *     before. Plain `mailto:` often does nothing for people who use webmail
 *     without a desktop email app, which is why Jody sending is the default.
 *
 * All channels read the (editable) textarea, so user edits propagate to
 * whichever channel they pick. Icons are inline SVG line icons (no emoji —
 * docs/codebase/ui-conventions.md); the accent color is a prop so the
 * calculator keeps its teal and My Trips its brand green.
 *
 * Analytics: `onChannel` fires for every channel the browser completes,
 * email with `{ email_method: 'app' }`. A Jody send is tracked by the server
 * where it happens (trip_share_channel_clicked, email_method 'jody';
 * docs/analytics-tracking.md §5c), so it does NOT fire `onChannel`.
 *
 * Consumers: features/tripCalculator/ShareButton.tsx,
 * features/my-trips/components/TripShareDialog.tsx,
 * features/share/SharedShareDialog.tsx.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useToast } from '../../hooks/useToast';

export type ShareChannel = 'copy_link' | 'copy' | 'email' | 'whatsapp' | 'x' | 'web_share';

/** Extra analytics detail for a channel (email: which way it went out). */
export interface ShareChannelDetail {
  email_method?: 'app';
}

/** What Jody's send reports back, per recipient. */
export interface ShareEmailResult {
  sent: string[];
  failed: string[];
  /** Addresses that asked not to get emails from Jody. */
  suppressed: string[];
}

export interface ShareChannelsProps {
  /** The share message (usually text + URL, maybe a password line). */
  shareText: string;
  /** The bare link, shown in the link row with Copy link (the main action). */
  shareUrl?: string;
  /** The share password, shown with its own copy button (password mode). */
  password?: string | null;
  /** Subject for the mailto: channel. */
  emailSubject: string;
  /** Title handed to the Web Share sheet. */
  shareTitle?: string;
  /** Accent for the primary Copy link button. Default: My Trips brand green. */
  accentColor?: string;
  /** Jody emails the trip for the user, with an optional note. Absent → the Email tile opens the email app. */
  sendEmail?: (to: string[], note: string) => Promise<ShareEmailResult>;
  /** Analytics hook — fired when a channel is used (not on render). */
  onChannel?: (channel: ShareChannel, detail?: ShareChannelDetail) => void;
  /** Called after a channel completed (e.g. to close the surrounding dialog). */
  onDone?: () => void;
  /** Toast copy after a successful clipboard copy of the message. */
  copyToastMessage?: string;
  /** Toast copy after a successful clipboard copy of the link. */
  copyLinkToastMessage?: string;
}

const RECIPIENTS_PER_SEND = 5; // mirrors backend services/trips/tripShareEmail.js
const EMAIL_RE = /^[^\s@,;<>"]+@[^\s@,;<>"]+\.[^\s@,;<>"]+$/;

/** Split what the user typed into addresses; the first bad one, if any. */
export function parseRecipientInput(input: string): { emails: string[]; invalid: string | null } {
  const emails: string[] = [];
  for (const part of input.split(/[\s,;]+/)) {
    const email = part.trim().toLowerCase();
    if (!email) continue;
    if (!EMAIL_RE.test(email)) return { emails, invalid: part.trim() };
    if (!emails.includes(email)) emails.push(email);
  }
  return { emails, invalid: null };
}

const icon = (path: React.ReactNode, size = 16) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    {path}
  </svg>
);
const stroke = { stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, fill: 'none' as const };

const COPY_PATH = <><rect x="9" y="9" width="12" height="12" rx="2" {...stroke} /><path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" {...stroke} /></>;
const ICONS = {
  copy: icon(COPY_PATH),
  link: icon(<><path d="M10 14a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1.5 1.5" {...stroke} /><path d="M14 10a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.5-1.5" {...stroke} /></>),
  email: icon(<><rect x="3" y="5" width="18" height="14" rx="2" {...stroke} /><path d="M3 7l9 6 9-6" {...stroke} /></>),
  whatsapp: icon(<><path d="M12 3a9 9 0 0 0-7.7 13.6L3 21l4.5-1.2A9 9 0 1 0 12 3z" {...stroke} /><path d="M9 8.5c0 4 2.5 6.5 6.5 6.5l.7-1.8-2-1-1 .7c-1-.5-1.6-1.1-2.1-2.1l.7-1-1-2L9 8.5z" {...stroke} /></>),
  x: icon(<path d="M4 4l16 16M20 4L4 20" {...stroke} />),
  web_share: icon(<><circle cx="6" cy="12" r="2.5" {...stroke} /><circle cx="17" cy="6" r="2.5" {...stroke} /><circle cx="17" cy="18" r="2.5" {...stroke} /><path d="M8.3 10.8l6.4-3.6M8.3 13.2l6.4 3.6" {...stroke} /></>),
};

/**
 * Web Share API only on coarse-pointer (touch-primary) devices — desktop
 * browsers that "support" it render a clunky half-screen panel.
 */
function shouldShowNativeShare(): boolean {
  if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return false;
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(pointer: coarse)').matches;
}

const LABEL_STYLE: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', margin: '0 0 6px' };

const ShareChannels: React.FC<ShareChannelsProps> = ({
  shareText,
  shareUrl,
  password,
  emailSubject,
  shareTitle = 'Jody',
  accentColor = '#157a55',
  sendEmail,
  onChannel,
  onDone,
  copyToastMessage = 'Paste it anywhere to share.',
  copyLinkToastMessage = 'Paste it anywhere to share.',
}) => {
  const [editableText, setEditableText] = useState(shareText);
  const [busy, setBusy] = useState<ShareChannel | 'password' | 'send' | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [recipients, setRecipients] = useState('');
  const [recipientError, setRecipientError] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const recipientsRef = useRef<HTMLInputElement | null>(null);
  const emailFormRef = useRef<HTMLFormElement | null>(null);
  const { showToast } = useToast();

  // The computed payload wins over stale edits when it changes (e.g. the
  // share mode switched and the password line appeared).
  useEffect(() => { setEditableText(shareText); }, [shareText]);
  // The panel opens at the very bottom of the dialog: bring all of it (Send
  // included) into view, then focus the field without a second jump.
  useEffect(() => {
    if (!emailOpen) return;
    emailFormRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    recipientsRef.current?.focus({ preventScroll: true });
  }, [emailOpen]);

  const copy = async (kind: 'copy_link' | 'copy' | 'password', value: string, onSuccess: () => void) => {
    if (busy) return;
    setBusy(kind);
    try {
      if (!navigator.clipboard) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(value);
      onSuccess();
    } catch {
      showToast({ type: 'error', title: 'Could not copy', message: 'Please select the text and copy manually.' });
    } finally {
      setBusy(null);
    }
  };

  const handleCopyLink = () => {
    if (!shareUrl) return;
    copy('copy_link', shareUrl, () => {
      onChannel?.('copy_link');
      showToast({
        type: 'success',
        title: 'Link copied',
        message: password ? `Send the password too: ${password}` : copyLinkToastMessage,
      });
      onDone?.();
    });
  };

  const handleCopyPassword = () => {
    if (!password) return;
    copy('password', password, () => {
      showToast({ type: 'success', title: 'Password copied', message: 'Send it together with the link.' });
    });
  };

  const handleCopyMessage = () => {
    copy('copy', editableText, () => {
      onChannel?.('copy');
      showToast({ type: 'success', title: 'Message copied', message: copyToastMessage });
      onDone?.();
    });
  };

  const openEmailApp = () => {
    onChannel?.('email', { email_method: 'app' });
    window.location.href = `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(editableText)}`;
    onDone?.();
  };

  const handleEmailTile = () => {
    if (sendEmail) setEmailOpen((open) => !open);
    else openEmailApp();
  };

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!sendEmail || busy) return;
    const { emails, invalid } = parseRecipientInput(recipients);
    if (invalid) { setRecipientError(`"${invalid}" doesn't look like an email address.`); return; }
    if (emails.length === 0) { setRecipientError('Add at least one email address.'); return; }
    if (emails.length > RECIPIENTS_PER_SEND) { setRecipientError(`You can send to up to ${RECIPIENTS_PER_SEND} people at a time.`); return; }
    setRecipientError(null);
    setBusy('send');
    try {
      const result = await sendEmail(emails, note);
      const notes = [
        result.suppressed.length ? `${result.suppressed.join(', ')} asked not to get emails from Jody.` : '',
        result.failed.length ? `Could not reach ${result.failed.join(', ')}.` : '',
      ].filter(Boolean).join(' ');
      if (result.sent.length === 0) {
        showToast({ type: 'error', title: 'Not sent', message: `${notes} Copy the link and send it another way.` });
        return;
      }
      const who = result.sent.length === 1 ? result.sent[0] : `${result.sent.length} people`;
      showToast({ type: 'success', title: 'Email sent', message: `Jody sent the trip to ${who}.${notes ? ` ${notes}` : ''}` });
      setRecipients('');
      setNote('');
      setEmailOpen(false);
      onDone?.();
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Could not send the email. Please try again, or copy the link instead.';
      showToast({ type: 'error', title: 'Not sent', message });
    } finally {
      setBusy(null);
    }
  };

  const handleWhatsApp = () => {
    onChannel?.('whatsapp');
    window.open(`https://wa.me/?text=${encodeURIComponent(editableText)}`, '_blank', 'noopener,noreferrer');
    onDone?.();
  };

  const handleX = () => {
    onChannel?.('x');
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(editableText)}`, '_blank', 'noopener,noreferrer');
    onDone?.();
  };

  const handleNativeShare = async () => {
    if (busy) return;
    setBusy('web_share');
    onChannel?.('web_share');
    try {
      await navigator.share({ title: shareTitle, text: editableText });
      onDone?.();
    } catch (err) {
      // User dismissed the sheet → keep the surface open for another channel.
      if ((err as DOMException)?.name !== 'AbortError') {
        showToast({ type: 'error', title: 'Could not share', message: 'Try a different option below.' });
      }
    } finally {
      setBusy(null);
    }
  };

  const canNativeShare = shouldShowNativeShare();
  const disabled = busy !== null;

  return (
    <div>
      {shareUrl && (
        <div style={{ marginBottom: 14 }}>
          <span style={LABEL_STYLE}>Link</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              readOnly
              value={shareUrl.replace(/^https?:\/\//, '')}
              onFocus={(e) => e.currentTarget.select()}
              aria-label="Share link"
              style={{
                flex: 1, minWidth: 0, padding: '0.6rem 0.7rem', fontFamily: 'inherit', fontSize: 13,
                color: '#0f172a', background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 10,
                boxSizing: 'border-box', textOverflow: 'ellipsis',
              }}
            />
            <button
              type="button"
              onClick={handleCopyLink}
              disabled={disabled}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, flexShrink: 0,
                padding: '0 14px', fontSize: 14, fontWeight: 800, color: '#fff', background: accentColor,
                border: 0, borderRadius: 10, cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: busy && busy !== 'copy_link' ? 0.6 : 1, fontFamily: 'inherit',
              }}
            >
              {ICONS.link}
              <span>{busy === 'copy_link' ? 'Copying…' : 'Copy link'}</span>
            </button>
          </div>
          {password && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <span style={{ fontSize: 12.5, color: '#64748b' }}>Password</span>
              <code style={{ fontSize: 15, fontWeight: 700, letterSpacing: 1, color: '#0f172a', background: '#f1f5f9', borderRadius: 6, padding: '2px 8px' }}>
                {password}
              </code>
              <button
                type="button"
                onClick={handleCopyPassword}
                disabled={disabled}
                aria-label="Copy password"
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28,
                  padding: 0, borderRadius: '50%', border: 0, background: '#f1f5f9', color: '#475569',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                }}
              >
                {icon(COPY_PATH, 14)}
              </button>
            </div>
          )}
        </div>
      )}

      {shareUrl && <span style={LABEL_STYLE}>Message</span>}
      <textarea
        value={editableText}
        onChange={(e) => setEditableText(e.target.value)}
        rows={4}
        spellCheck={false}
        aria-label="Share message"
        style={{
          width: '100%',
          resize: 'vertical',
          padding: '0.6rem 0.7rem',
          fontFamily: 'inherit',
          fontSize: 13,
          lineHeight: 1.5,
          color: '#0f172a',
          background: '#f8fafc',
          border: '1px solid #cbd5e1',
          borderRadius: 8,
          boxSizing: 'border-box',
        }}
      />

      <button
        type="button"
        onClick={handleCopyMessage}
        disabled={disabled}
        style={{
          marginTop: '0.6rem',
          width: '100%',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '0.65rem 1rem',
          fontSize: 14,
          fontWeight: 700,
          // Without a link row (a caller that passes none), Copy message is
          // the only copy action and keeps the filled main-button look.
          color: shareUrl ? accentColor : '#fff',
          background: shareUrl ? '#fff' : accentColor,
          border: shareUrl ? `1.5px solid ${accentColor}` : 0,
          borderRadius: 10,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: busy && busy !== 'copy' ? 0.6 : 1,
          fontFamily: 'inherit',
        }}
      >
        {ICONS.copy}
        <span>{busy === 'copy' ? 'Copying…' : 'Copy message'}</span>
      </button>

      <div style={{ marginTop: '0.85rem', display: 'flex', alignItems: 'center', gap: 8 }}>
        <hr style={{ flex: 1, border: 0, borderTop: '1px solid #e2e8f0' }} />
        <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, letterSpacing: 0.4 }}>OR SEND THE MESSAGE BY</span>
        <hr style={{ flex: 1, border: 0, borderTop: '1px solid #e2e8f0' }} />
      </div>

      <div
        style={{
          marginTop: '0.65rem',
          display: 'grid',
          gridTemplateColumns: `repeat(${canNativeShare ? 4 : 3}, minmax(0, 1fr))`,
          gap: 8,
        }}
      >
        <ChannelButton
          icon={ICONS.email}
          label="Email"
          onClick={handleEmailTile}
          disabled={disabled}
          active={emailOpen}
          expanded={sendEmail ? emailOpen : undefined}
          accentColor={accentColor}
        />
        <ChannelButton icon={ICONS.whatsapp} label="WhatsApp" onClick={handleWhatsApp} disabled={disabled} accentColor={accentColor} />
        <ChannelButton icon={ICONS.x} label="X" onClick={handleX} disabled={disabled} accentColor={accentColor} />
        {canNativeShare && (
          <ChannelButton icon={ICONS.web_share} label="More…" onClick={handleNativeShare} disabled={disabled} accentColor={accentColor} />
        )}
      </div>

      {sendEmail && emailOpen && (
        <form
          ref={emailFormRef}
          onSubmit={handleSend}
          noValidate
          style={{ marginTop: 10, padding: '12px 12px 10px', border: '1px solid #e2e8f0', borderRadius: 10, background: '#f8fafc' }}
        >
          <label htmlFor="share-email-to" style={{ ...LABEL_STYLE, color: '#0f172a', fontSize: 13 }}>Send by email</label>
          <input
            id="share-email-to"
            ref={recipientsRef}
            // Text + email keyboard, not type="email": a controlled email
            // field lets the browser strip the spaces typed after commas.
            type="text"
            inputMode="email"
            autoCapitalize="none"
            spellCheck={false}
            autoComplete="email"
            value={recipients}
            onChange={(e) => { setRecipients(e.target.value); setRecipientError(null); }}
            placeholder="name@example.com, another@example.com"
            aria-invalid={!!recipientError}
            aria-describedby="share-email-help"
            style={{
              width: '100%', boxSizing: 'border-box', padding: '0.55rem 0.7rem', fontFamily: 'inherit', fontSize: 13,
              color: '#0f172a', background: '#fff', borderRadius: 8,
              border: recipientError ? '1.5px solid #cf5a3c' : '1px solid #cbd5e1',
            }}
          />
          {recipientError && (
            <div role="alert" style={{ fontSize: 12, lineHeight: 1.45, margin: '6px 0 0', color: '#cf5a3c' }}>
              {recipientError}
            </div>
          )}
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={2}
            maxLength={2000}
            placeholder="Add a note (optional)"
            aria-label="Note for the email (optional)"
            style={{
              width: '100%', boxSizing: 'border-box', marginTop: 8, padding: '0.55rem 0.7rem', resize: 'vertical',
              fontFamily: 'inherit', fontSize: 13, lineHeight: 1.5, color: '#0f172a', background: '#fff',
              border: '1px solid #cbd5e1', borderRadius: 8,
            }}
          />
          <div id="share-email-help" style={{ fontSize: 12, lineHeight: 1.45, margin: '6px 0 10px', color: '#64748b' }}>
            {`Jody emails a link to this trip in your name, with your note, and replies come to you. Up to ${RECIPIENTS_PER_SEND} addresses.`}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <button
              type="submit"
              disabled={disabled}
              style={{
                padding: '0.5rem 1.1rem', fontSize: 14, fontWeight: 700, color: '#fff', background: accentColor,
                border: 0, borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: busy && busy !== 'send' ? 0.6 : 1, fontFamily: 'inherit',
              }}
            >
              {busy === 'send' ? 'Sending…' : 'Send'}
            </button>
            <button
              type="button"
              onClick={openEmailApp}
              disabled={disabled}
              style={{ background: 'none', border: 0, padding: 0, color: accentColor, fontSize: 13, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}
            >
              Open my email app instead
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

const ChannelButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  /** Highlighted while its panel is open (Email). */
  active?: boolean;
  /** aria-expanded, for a tile that opens a panel. */
  expanded?: boolean;
  accentColor: string;
}> = ({ icon: channelIcon, label, onClick, disabled, active, expanded, accentColor }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-expanded={expanded}
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 4,
      padding: '0.55rem 0.4rem',
      fontSize: 12,
      fontWeight: 600,
      color: '#0f172a',
      background: active ? '#fff' : '#f8fafc',
      border: active ? `1.5px solid ${accentColor}` : '1px solid #e2e8f0',
      borderRadius: 8,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      fontFamily: 'inherit',
    }}
  >
    <span style={{ display: 'inline-flex', color: active ? accentColor : '#475569' }}>{channelIcon}</span>
    <span>{label}</span>
  </button>
);

export default ShareChannels;
